// LeadAssistantPanel — the "Live AI Assistant" floating panel per
// the spec. Compact chat-style UI. Ask anything; the backend
// resolves to a structured intent + real source citation.
//
// Sits at the bottom of the drawer — the "if in doubt, ask" fall-
// back after all the panels above.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const QUICK = [
  { key: 'summarize',     label: 'Summarize'         },
  { key: 'next_step',     label: 'Next step'         },
  { key: 'budget',        label: 'Budget?'           },
  { key: 'medications',   label: 'Meds?'             },
  { key: 'doctors',       label: 'Doctors?'          },
  { key: 'objections',    label: 'Objections?'       },
  { key: 'draft_text',    label: 'Draft text'        },
  { key: 'draft_email',   label: 'Draft email'       },
];

const QUICK_TO_Q = {
  summarize:   'Summarize this lead',
  next_step:   "What's the next step?",
  budget:      "What's their budget?",
  medications: 'What medications are they on?',
  doctors:     'Who is their doctor?',
  objections:  'What objections might come up?',
  draft_text:  'Draft a text',
  draft_email: 'Draft an email',
};

function CopyBox({ text }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div style={{
      background: '#f8fafc', border: '1px solid #e2e8f0',
      borderRadius: 8, padding: 12, marginTop: 6,
    }}>
      <pre style={{
        whiteSpace: 'pre-wrap', color: '#0f172a',
        fontFamily: 'inherit', fontSize: 12, lineHeight: 1.55, margin: 0,
      }}>{text}</pre>
      <button
        onClick={async () => {
          try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
        }}
        style={{
          marginTop: 8, background: '#0891b2', border: 'none', color: '#ffffff',
          padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
        }}
      >{copied ? 'Copied ✓' : 'Copy'}</button>
    </div>
  );
}

export default function LeadAssistantPanel({ lead, callHistory = [] }) {
  const [q, setQ] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [answer, setAnswer] = React.useState(null);
  const [error, setError] = React.useState(null);

  const submit = async (question) => {
    setLoading(true); setError(null);
    try {
      const r = await fetch('/api/leads/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearer()) },
        body: JSON.stringify({ lead, callHistory, question }),
      });
      const j = await r.json();
      if (r.ok && j.ok) setAnswer(j);
      else { setError(j.error || `HTTP ${r.status}`); setAnswer(null); }
    } catch (err) { setError(err.message); setAnswer(null); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      background: '#0f172a',
      border: '1px solid #1e293b',
      borderRadius: 10, padding: 12,
      color: '#e2e8f0',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#22d3ee', fontWeight: 800 }}>
          Ask about this lead
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>Sourced answers, no guesses</div>
      </div>

      {/* Quick chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
        {QUICK.map(x => (
          <button
            key={x.key}
            onClick={() => { setQ(QUICK_TO_Q[x.key]); submit(QUICK_TO_Q[x.key]); }}
            style={{
              background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0',
              padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}
          >{x.label}</button>
        ))}
      </div>

      {/* Free ask */}
      <form
        onSubmit={(e) => { e.preventDefault(); if (q.trim()) submit(q.trim()); }}
        style={{ display: 'flex', gap: 6 }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask anything about this lead…"
          style={{
            flex: 1, background: '#020617', color: '#e2e8f0',
            border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 12,
          }}
        />
        <button
          type="submit" disabled={loading || !q.trim()}
          style={{
            background: '#0891b2', border: 'none', color: '#ffffff',
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 800,
            cursor: loading ? 'wait' : 'pointer', opacity: (loading || !q.trim()) ? 0.6 : 1,
          }}
        >{loading ? '…' : 'Ask'}</button>
      </form>

      {error && (
        <div style={{ color: '#fca5a5', marginTop: 10, fontSize: 11 }}>{error}</div>
      )}

      {answer && (
        <div style={{ marginTop: 12 }}>
          {answer.headline && (
            <div style={{ fontSize: 12, fontWeight: 800, color: '#e2e8f0', marginBottom: 6 }}>
              {answer.headline}
            </div>
          )}
          {answer.answer && (
            <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.55 }}>{answer.answer}</div>
          )}
          {answer.draft && <CopyBox text={answer.draft}/>}
          {answer.supported && (
            <ul style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.8, paddingLeft: 18, marginTop: 4 }}>
              {answer.supported.map(s => <li key={s}>{s}</li>)}
            </ul>
          )}
          {answer.source?.length > 0 && (
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 10, letterSpacing: '0.06em' }}>
              Source: {answer.source.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
